// Lightweight CSS-only motion previews for each game hub card. No assets, GPU-cheap.
const G = "#39FF14", B = "#00F0FF", R = "#EF4444", Y = "#F5D90A", P = "#FF5CA8";

const anim = (name, dur, ease = "linear", delay = 0) => ({ animation: `${name} ${dur}s ${ease} infinite`, animationDelay: `${delay}s` });
const dot = (c, s = 10) => ({ width: s, height: s, borderRadius: "50%", background: c, boxShadow: `0 0 10px ${c}` });
const box = (c, w = 16, h = 10) => ({ width: w, height: h, borderRadius: 3, background: c, boxShadow: `0 0 10px ${c}aa` });

function Ring({ fast }) {
  return (
    <div className="gp-screen">
      <div className="gp-c" style={{ width: 26, height: 26, borderRadius: "50%", border: `2px solid ${G}`, opacity: 0.55 }} />
      <div className="gp-c" style={{ width: 58, height: 58, borderRadius: "50%", border: "2px solid #fff", ...anim("gp-ring", fast ? 1.1 : 1.7, "ease-in") }} />
    </div>
  );
}

function Lanes({ colors }) {
  return (
    <div className="gp-screen" style={{ display: "flex", padding: "0 14%" }}>
      {colors.map((c, i) => (
        <div key={i} style={{ flex: 1, position: "relative" }}>
          <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", ...box(c, 14, 8), ...anim("gp-fall", 1.4 + i * 0.15, "linear", i * 0.35) }} />
        </div>
      ))}
      <div style={{ position: "absolute", left: "10%", right: "10%", bottom: "22%", borderTop: `1px dashed ${G}66` }} />
    </div>
  );
}

function Dodge() {
  return (
    <div className="gp-screen">
      <div style={{ position: "absolute", top: "-28%", ...box(R, 16, 9), left: "26%", ...anim("gp-fall", 1.3) }} />
      <div style={{ position: "absolute", top: "-28%", ...box(R, 16, 9), left: "58%", ...anim("gp-fall", 1.5, "linear", 0.6) }} />
      <div style={{ position: "absolute", bottom: "16%", ...box(G, 18, 9), ...anim("gp-dodge", 1.6, "ease-in-out") }} />
    </div>
  );
}

const SCENES = {
  bullseye: () => <Ring />,
  "impossible-mode": () => <Ring fast />,
  cascade: () => <Lanes colors={[G, B, Y, P]} />,
  sprint: () => <Dodge />,
  dodge: () => <Dodge />,
  "ghost-tap": () => (
    <div className="gp-screen">
      <div style={{ position: "absolute", top: "50%", transform: "translateY(-50%)", ...dot(G, 12), ...anim("gp-cross", 2.2) }} />
      <div style={{ position: "absolute", left: "50%", top: "18%", bottom: "18%", width: 6, background: "#ffffff22", transform: "translateX(-50%)" }} />
    </div>
  ),
  "dual-mind": () => (
    <div className="gp-screen">
      <div className="gp-c" style={{ left: "32%", ...dot(G, 14), ...anim("gp-pulse", 1.4, "ease-in-out") }} />
      <div className="gp-c" style={{ left: "68%", ...dot(B, 14), ...anim("gp-pulse", 1.4, "ease-in-out", 0.7) }} />
    </div>
  ),
  blink: () => (
    <div className="gp-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span className="font-mono font-black" style={{ fontSize: 34, color: G, ...anim("gp-blink", 1.6) }}>7</span>
    </div>
  ),
  nerve: () => (
    <div className="gp-screen" style={{ display: "flex", alignItems: "center", padding: "0 12%" }}>
      <div style={{ position: "relative", flex: 1, height: 10, borderRadius: 6, background: "#ffffff12", overflow: "hidden" }}>
        <div style={{ height: "100%", borderRadius: 6, background: G, boxShadow: `0 0 12px ${G}`, ...anim("gp-fill", 1.6, "ease-in") }} />
      </div>
      <div style={{ width: 3, height: 20, background: R, marginLeft: 4, boxShadow: `0 0 10px ${R}` }} />
    </div>
  ),
  "color-trap": () => (
    <div className="gp-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
      <span className="font-display font-black" style={{ fontSize: 20, color: G, ...anim("gp-blink", 1.8) }}>RED</span>
      <span className="font-display font-black" style={{ fontSize: 20, color: B, ...anim("gp-blink", 1.8, "linear", 0.9) }}>BLUE</span>
    </div>
  ),
  "odd-one-out": () => (
    <div className="gp-screen" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 5, padding: "18% 22%" }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} style={{ borderRadius: 3, background: i === 5 ? G : "#2a2a2a", ...(i === 5 ? anim("gp-glow", 1.4, "ease-in-out") : {}) }} />
      ))}
    </div>
  ),
  flip: () => (
    <div className="gp-screen">
      <div className="gp-c" style={{ ...box(G, 44, 28), ...anim("gp-swap", 2) }} />
      <div className="gp-c" style={{ ...box(B, 44, 28), animation: "gp-swap 2s linear infinite", animationDelay: "1s" }} />
    </div>
  ),
  mirror: () => (
    <div className="gp-screen" style={{ display: "flex", justifyContent: "center", gap: 14, alignItems: "center" }}>
      {[0, 1].map((g) => (
        <div key={g} style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 3 }}>
          {[0, 1, 2, 3].map((c) => {
            const active = g === 0 ? c === 1 : c === 0;
            return <div key={c} style={{ width: 11, height: 11, borderRadius: 2, background: active ? G : "#2a2a2a", ...(active ? anim("gp-glow", 1.6, "ease-in-out", g * 0.1) : {}) }} />;
          })}
        </div>
      ))}
    </div>
  ),
  pulse: () => (
    <div className="gp-screen">
      <div className="gp-c" style={{ ...dot(G, 18), ...anim("gp-pulse", 1.8, "ease-in-out") }} />
    </div>
  ),
  peripheral: () => (
    <div className="gp-screen">
      <div className="gp-c" style={{ ...dot("#fff", 6) }} />
      <div style={{ position: "absolute", left: "14%", top: "24%", ...dot(G, 9), ...anim("gp-blink", 1.7) }} />
      <div style={{ position: "absolute", right: "16%", bottom: "22%", ...dot(G, 9), ...anim("gp-blink", 1.7, "linear", 0.85) }} />
    </div>
  ),
  tracking: () => (
    <div className="gp-screen">
      <div className="gp-c" style={{ width: 20, height: 20, borderRadius: "50%", border: `2px solid ${G}66`, ...anim("gp-orbit", 2.4) }} />
      <div className="gp-c" style={{ ...dot(G, 8), animation: "gp-orbit 2.4s linear infinite", animationDelay: "0.12s" }} />
    </div>
  ),
  schulte: () => (
    <div className="gp-screen" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 3, padding: "14% 26%" }}>
      {[3, 9, 1, 7, 5, 2, 8, 4].map((n, i) => (
        <div key={i} className="font-mono" style={{ fontSize: 9, textAlign: "center", color: i === 2 ? G : "#666", ...(i === 2 ? anim("gp-glow", 1.4, "ease-in-out") : {}) }}>{n}</div>
      ))}
    </div>
  ),
  "target-rush": () => (
    <div className="gp-screen">
      <div style={{ position: "absolute", left: "24%", top: "30%", ...dot(G, 14), ...anim("gp-pop", 1.4) }} />
      <div style={{ position: "absolute", left: "62%", top: "52%", ...dot(G, 14), ...anim("gp-pop", 1.4, "linear", 0.5) }} />
      <div style={{ position: "absolute", left: "44%", top: "22%", ...dot(G, 12), ...anim("gp-pop", 1.4, "linear", 0.9) }} />
    </div>
  ),
  "reflex-duel": () => (
    <div className="gp-screen" style={{ display: "flex", gap: 8, padding: 12 }}>
      <div style={{ flex: 1, borderRadius: 6, background: G, ...anim("gp-blink", 1.6) }} />
      <div style={{ flex: 1, borderRadius: 6, background: G, ...anim("gp-blink", 1.6, "linear", 0.8) }} />
    </div>
  ),
  "speed-round": () => (
    <div className="gp-screen" style={{ padding: 12 }}>
      <div style={{ width: "100%", height: "100%", borderRadius: 8, background: G, ...anim("gp-blink", 0.9) }} />
    </div>
  ),
  "sequence-memory": () => (
    <div className="gp-screen" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 4, padding: "14% 34%" }}>
      {Array.from({ length: 9 }).map((_, i) => (
        <div key={i} style={{ borderRadius: 3, background: G, ...anim("gp-glow", 1.8, "ease-in-out", ((i % 3) + Math.floor(i / 3)) * 0.2) }} />
      ))}
    </div>
  ),
  "number-memory": () => (
    <div className="gp-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span className="font-mono font-black" style={{ fontSize: 26, color: G, letterSpacing: 2, ...anim("gp-blink", 1.8) }}>294</span>
    </div>
  ),
  "verbal-memory": () => (
    <div className="gp-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span className="font-display font-black" style={{ fontSize: 20, color: G, ...anim("gp-swap", 2) }}>NEW</span>
      <span className="font-display font-black" style={{ position: "absolute", fontSize: 20, color: B, animation: "gp-swap 2s linear infinite", animationDelay: "1s" }}>SEEN</span>
    </div>
  ),
  "chimp-test": () => (
    <div className="gp-screen">
      {[["24%", "34%", "1"], ["58%", "54%", "2"], ["44%", "22%", "3"]].map(([l, t, n], i) => (
        <div key={i} className="font-mono font-black" style={{ position: "absolute", left: l, top: t, color: G, fontSize: 16, ...anim("gp-pop", 1.5, "linear", i * 0.4) }}>{n}</div>
      ))}
    </div>
  ),
  "visual-memory": () => (
    <div className="gp-screen" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4, padding: "16% 24%" }}>
      {Array.from({ length: 8 }).map((_, i) => {
        const lit = [1, 4, 6].includes(i);
        return <div key={i} style={{ borderRadius: 3, background: lit ? G : "#2a2a2a", ...(lit ? anim("gp-glow", 1.6, "ease-in-out") : {}) }} />;
      })}
    </div>
  ),
  "aim-trainer": () => (
    <div className="gp-screen">
      <div style={{ position: "absolute", left: "28%", top: "34%", ...dot(G, 12), ...anim("gp-pop", 1.2) }} />
      <div style={{ position: "absolute", left: "60%", top: "50%", ...dot(G, 12), ...anim("gp-pop", 1.2, "linear", 0.4) }} />
      <div style={{ position: "absolute", left: "46%", top: "20%", ...dot(G, 10), ...anim("gp-pop", 1.2, "linear", 0.8) }} />
    </div>
  ),
  "typing-speed": () => (
    <div className="gp-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span className="font-mono font-bold" style={{ fontSize: 14, color: "#888" }}>type</span>
      <span style={{ width: 2, height: 16, background: G, marginLeft: 2, ...anim("gp-blink", 0.8) }} />
      <span className="font-mono font-bold" style={{ fontSize: 12, color: G, marginLeft: 8 }}>WPM</span>
    </div>
  ),
  "click-speed": () => (
    <div className="gp-screen">
      <div className="gp-c" style={{ width: 34, height: 34, borderRadius: "50%", border: `2px solid ${G}`, ...anim("gp-ring", 0.9, "ease-out") }} />
      <div className="gp-c" style={{ ...dot(G, 12), ...anim("gp-pulse", 0.7, "ease-in-out") }} />
      <span className="font-mono font-bold" style={{ position: "absolute", right: "14%", bottom: "18%", fontSize: 11, color: G, ...anim("gp-blink", 0.8) }}>CPS</span>
    </div>
  ),
  "spacebar-speed": () => (
    <div className="gp-screen" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 100, height: 26, borderRadius: 5, border: `2px solid ${G}`, display: "flex", alignItems: "center", justifyContent: "center", ...anim("gp-blink", 0.6) }}>
        <span className="font-mono font-bold" style={{ fontSize: 9, color: G, letterSpacing: 2 }}>SPACE</span>
      </div>
    </div>
  ),
};

function Default() {
  return (
    <div className="gp-screen">
      <div className="gp-c" style={{ ...dot(G, 16), ...anim("gp-pulse", 1.8, "ease-in-out") }} />
    </div>
  );
}

export default function GamePreview({ type, icon: Icon, className, showIcon = true }) {
  const Scene = SCENES[type] || Default;
  return (
    <div className={className ? `${className} gp-host` : "gp-host relative h-20 rounded-xl overflow-hidden mb-4 border border-white/8"}
      style={className ? undefined : { background: "radial-gradient(ellipse 80% 90% at 50% 120%, rgba(57,255,20,0.12), #0a0a0a 70%)" }}
      data-testid={`game-preview-${type}`} aria-hidden="true">
      <Scene />
      {showIcon && Icon && (
        <div className="absolute left-2 top-2 w-7 h-7 rounded-lg bg-black/55 backdrop-blur-sm flex items-center justify-center border border-white/10">
          <Icon size={15} className="text-[#39FF14]" />
        </div>
      )}
    </div>
  );
}
